import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createVNode as _createVNode, resolveComponent as _resolveComponent, createBlock as _createBlock, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, Fragment as _Fragment } from "vue"

const _hoisted_1 = { id: "modal-container-element" }
const _hoisted_2 = { key: 1 }
const _hoisted_3 = { class: "search-container" }
const _hoisted_4 = ["value"]
const _hoisted_5 = ["rows", "total-items", "current-page", "loading"]

import { EPINIO_TYPES } from '../types';
import { useStore } from 'vuex';
import { computed, ref, watch } from 'vue';
import { useBuilderImages } from '../queries/useBuilderImagesQueries';
import Masthead from '@shell/components/ResourceList/Masthead';
import { debounce } from 'lodash';
import ImageModal from '../components/images/ImageModal.vue';
import { makeActionMenu } from '../utils/table-formatters';
import ImageDeleteModal from '../components/images/ImageDeleteModal.vue';
import { ResourceQueryOptions, ListResourceRequestParams, ResourceTableRow } from '../models/resource/ui-types';
import { BuilderImage } from '../models/builderimage/ui-types';
import { useUser } from '../queries/useUserQueries';


export default /*@__PURE__*/_defineComponent({
  __name: 'builderimages',
  props: {
    schema: {}
  },
  setup(__props: any) {

 // Keep for compatibility

const store = useStore();

const imageModal = ref<InstanceType<typeof ImageModal> | null>(null);
const deleteModal = ref<InstanceType<typeof ImageDeleteModal> | null>(null);

const resource: string = EPINIO_TYPES.BUILDER_IMAGE;

const { data: user, isError: isErrorUser, error: userError } = useUser(store);

const requestParams = ref<ListResourceRequestParams>({
  page: 1,
  pageSize: 10,
  search: '',
});

const requestOptions = ref<ResourceQueryOptions>({
  enabled: true,
  polling: true,
  isTablePagination: true,
});

const searchQuery = ref<string>('');

watch(searchQuery, (newQuery) => {
  onSearch(newQuery);
});

const onSearch = debounce(async (query: string) => {
  requestParams.value.page = 1;
  requestParams.value.search = query;
}, 500);

const {data: builderImages, isLoading: isLoadingBuilderImages, isError: isErrorBuilderImages, error: builderImagesError} = useBuilderImages(store, requestParams, requestOptions);

const canEdit = computed(() => {
  return user.value?.permissions?.builderimage_write;
});
const canDelete = canEdit;
const canCreate = canEdit;

const openDeleteModal = (builderImage: BuilderImage) => {
  deleteModal.value?.openDelete(builderImage);
};

const openEditModal = (builderImage: BuilderImage) => {
  imageModal.value?.openEdit(builderImage);
};

const displayRows = computed(() => {
  if (!builderImages.value) {
    return [];
  }

  const rows: ResourceTableRow<BuilderImage>[] = (builderImages.value.items ?? []).map((bi) => ({
    ...bi,
    id: bi.meta.name,
    availableActions: [
      {
        label: 'Edit',
        action: () => openEditModal(bi),
        enabled: canEdit.value,
        visible: canEdit.value,
      },
      {
        label: 'Delete',
        action: () => openDeleteModal(bi),
        enabled: canDelete.value && !bi.default,
        visible: canDelete.value && !bi.default,
        danger: true,
      },
    ],
    canDelete: canDelete.value,
  }));
  return rows;
});

const columns = [
  {
    field: 'nameDisplay',
    label: 'Name',
    formatter: (_v: any, row: BuilderImage) => {
      const el = document.createElement('a');

      el.textContent = row.meta?.name || '';
      el.style.cursor = 'pointer';
      el.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        imageModal.value?.openView(row, !!canEdit.value);
      });

      return el;
    }
  },
  {
    field: 'description',
    label: 'Description'
  },
  {
    field: 'image',
    label: 'Image'
  },
  {
    field:     'meta.createdAt',
    label:     'Age',
    formatter: 'age'
  }
];

return (_ctx: any,_cache: any) => {
  const _component_Banner = _resolveComponent("Banner")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createVNode(_unref(Masthead), {
        schema: _ctx.schema,
        resource: _unref(resource)
      }, {
        createButton: _withCtx(() => [
          (_unref(canCreate))
            ? (_openBlock(), _createElementBlock("trailhand-button", {
                key: 0,
                variant: "primary",
                size: "large",
                onButtonClick: _cache[0] || (_cache[0] = ($event: any) => (imageModal.value?.openCreate()))
              }, _toDisplayString(_ctx.t('generic.create')), 33))
            : (_openBlock(), _createElementBlock("div", _hoisted_2))
        ]),
        _: 1
      }, 8, ["schema", "resource"]),
      _cache[3] || (_cache[3] = _createTextVNode()),
      (_unref(isErrorUser))
        ? (_openBlock(), _createBlock(_component_Banner, {
            key: 0,
            color: "error",
            label: _unref(userError)?.message || _ctx.t('epinio.user.errors.fetch')
          }, null, 8, ["label"]))
        : _createCommentVNode("", true),
      _cache[4] || (_cache[4] = _createTextVNode()),
      (_unref(isErrorBuilderImages))
        ? (_openBlock(), _createBlock(_component_Banner, {
            key: 1,
            color: "error",
            label: _unref(builderImagesError)?.message || _ctx.t('epinio.builderImages.errors.fetch')
          }, null, 8, ["label"]))
        : _createCommentVNode("", true),
      _cache[5] || (_cache[5] = _createTextVNode()),
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("trailhand-text-input", {
          value: searchQuery.value,
          placeholder: "Search...",
          onTextInputChange: _cache[1] || (_cache[1] = (e) => searchQuery.value = e.detail.value)
        }, null, 40, _hoisted_4)
      ]),
      _cache[6] || (_cache[6] = _createTextVNode()),
      _createElementVNode("trailhand-table", {
        ref: (el) => { if (el) el.renderActions = _unref(makeActionMenu); },
        rows: displayRows.value,
        columns: columns,
        "server-side": true,
        searchable: false,
        "total-items": _unref(builderImages)?.totalItems ?? 0,
        "current-page": requestParams.value.page,
        loading: _unref(isLoadingBuilderImages),
        "key-field": "id",
        onPageChange: _cache[2] || (_cache[2] = (e) => { requestParams.value.page = e.detail.page; })
      }, null, 40, _hoisted_5)
    ]),
    _cache[7] || (_cache[7] = _createTextVNode()),
    _createVNode(ImageModal, {
      ref_key: "imageModal",
      ref: imageModal
    }, null, 512),
    _cache[8] || (_cache[8] = _createTextVNode()),
    _createVNode(ImageDeleteModal, {
      ref_key: "deleteModal",
      ref: deleteModal
    }, null, 512)
  ], 64))
}
}

})